import { useId, type ReactNode } from 'react';
import * as RTabs from '@radix-ui/react-tabs';
import { motion } from 'motion/react';
import { cx } from '@/lib/cx';

export interface TabItem {
  value: string;
  label: ReactNode;
  count?: number | null;
  icon?: ReactNode;
  hidden?: boolean;
}

export interface TabsProps {
  value: string;
  onValueChange: (v: string) => void;
  items: TabItem[];
  children: ReactNode;
  label: string;
  className?: string;
}

/**
 * Tabs with roving focus (arrow keys) from Radix and a shared-layout underline.
 * The list scrolls horizontally on narrow screens rather than wrapping.
 */
export function Tabs({ value, onValueChange, items, children, label, className }: TabsProps) {
  const layoutId = useId();
  return (
    <RTabs.Root value={value} onValueChange={onValueChange} className={cx('tabs', className)} activationMode="manual">
      <div className="tabs-list-scroll">
        <RTabs.List className="tabs-list" aria-label={label}>
          {items
            .filter((t) => !t.hidden)
            .map((t) => (
              <RTabs.Trigger key={t.value} value={t.value} className="tab">
                {t.icon && <span className="tab-icon" aria-hidden>{t.icon}</span>}
                <span>{t.label}</span>
                {t.count !== undefined && t.count !== null && <span className="tab-count tabular">{t.count}</span>}
                {value === t.value && <motion.span layoutId={`tab-ind-${layoutId}`} className="tab-indicator" transition={{ type: 'spring', stiffness: 520, damping: 42 }} />}
              </RTabs.Trigger>
            ))}
        </RTabs.List>
      </div>
      {children}
    </RTabs.Root>
  );
}

export function TabPanel({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  return (
    <RTabs.Content value={value} className={cx('tab-panel', className)}>
      {children}
    </RTabs.Content>
  );
}
